import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import type {
  CreateProductPayload,
  Product,
  UnitOfMeasure,
  Warehouse,
} from "@/services/types/products";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const NO_BASE_UOM_VALUE = "__none__";

const addProductFormSchema = z.object({
  name: z.string().trim().min(1, "Product name is required"),
  sku: z.string().trim().min(1, "SKU ID is required"),
  base_stock_uom: z.string(),
  price: z.coerce.number().min(0, "Selling price must be zero or greater"),
  storage_type: z.enum(["general", "dry", "cold"]),
  initial_stocks: z.array(
    z.object({
      warehouse_id: z.number(),
      warehouse_name: z.string(),
        quantity: z.coerce.number().min(0, "Stock must be zero or greater"),
      }),
  ),
}).superRefine((values, ctx) => {
  if (
    !values.base_stock_uom &&
    values.initial_stocks.some((stock) => stock.quantity > 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["base_stock_uom"],
      message: "Base unit of measure is required when initial stock is entered",
    });
  }
});

type AddProductFormValues = z.infer<typeof addProductFormSchema>;

function buildDefaultValues(
  warehouses: Warehouse[],
  duplicateProduct?: Product | null,
): AddProductFormValues {
  return {
    name: duplicateProduct?.name ?? "",
    sku: "",
    base_stock_uom: duplicateProduct?.base_stock_uom
      ? String(duplicateProduct.base_stock_uom)
      : "",
    price: duplicateProduct?.price ?? 0,
    storage_type: duplicateProduct?.storage_type ?? "general",
    initial_stocks: warehouses.map((warehouse) => ({
      warehouse_id: warehouse.id,
      warehouse_name: warehouse.name,
      quantity: 0,
    })),
  };
}

export function AddProductDrawer({
  open,
  onOpenChange,
  warehouses,
  uoms,
  duplicateProduct,
  isSubmitting,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouses: Warehouse[];
  uoms: UnitOfMeasure[];
  duplicateProduct?: Product | null;
  isSubmitting: boolean;
  onSubmit: (payload: CreateProductPayload) => Promise<void>;
}) {
  const defaultValues = useMemo(
    () => buildDefaultValues(warehouses, duplicateProduct),
    [duplicateProduct, warehouses],
  );
  const form = useForm<AddProductFormValues>({
    resolver: zodResolver(addProductFormSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form, open]);

  const handleSubmit = async (values: AddProductFormValues) => {
    await onSubmit({
      name: values.name.trim(),
            sku: values.sku.trim(),
      base_stock_uom: values.base_stock_uom ? Number(values.base_stock_uom) : null,
      price: values.price,
      storage_type: values.storage_type,
      initial_stocks: values.initial_stocks.map((stock) => ({
        warehouse_id: stock.warehouse_id,
        quantity: stock.quantity,
      })),
    });
    form.reset(defaultValues);
    onOpenChange(false);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) {
          form.reset(defaultValues);
        }
      }}
    >
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-hidden border-l bg-background p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b px-6 py-5 text-left">
          <SheetTitle className="text-xl">
            {duplicateProduct ? "Duplicate Product" : "Add Product"}
          </SheetTitle>
          <SheetDescription>
            {duplicateProduct
              ? "Create a copy with a new SKU. Opening stock is not copied."
              : "Create a product and set opening stock by warehouse."}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex h-full min-h-0 flex-col"
          >
            <div className="flex-1 min-h-0 space-y-6 overflow-y-auto px-6 py-5">
              <div className="grid gap-5 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Product Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter product name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sku"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SKU ID</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter SKU ID" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="base_stock_uom"
                  render={({ field }) => {
                    const selectedBaseUom = uoms.find(
                      (uom) => String(uom.id) === field.value,
                    );

                    return (
                      <FormItem>
                        <FormLabel>Base Unit of Measure</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value || NO_BASE_UOM_VALUE}
                            onValueChange={(value) =>
                              field.onChange(value === NO_BASE_UOM_VALUE ? "" : value)
                            }
                          >
                            <SelectTrigger className="w-full">
                              <span
                                className={
                                  selectedBaseUom ? "truncate" : "truncate text-muted-foreground"
                                }
                              >
                                {selectedBaseUom
                                  ? `${selectedBaseUom.name} (${selectedBaseUom.code})`
                                  : "Select UOM"}
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem
                                value={NO_BASE_UOM_VALUE}
                                className="text-muted-foreground"
                              >
                                Select UOM
                              </SelectItem>
                              {uoms.map((uom) => (
                                <SelectItem key={uom.id} value={String(uom.id)}>
                                  {uom.name} ({uom.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Selling Price</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          onChange={(event) => field.onChange(event.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="storage_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Storage Type</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select storage type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="dry">Dry</SelectItem>
                            <SelectItem value="cold">Cold</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <h3 className="font-medium text-base">Initial Stock</h3>

                <div className="space-y-3">
                  {warehouses.map((warehouse, index) => (
                    <div
                      key={warehouse.id}
                      className="grid gap-3 rounded-2xl border border-border/70 bg-muted/20 p-4 md:grid-cols-[minmax(0,1fr)_180px]"
                    >
                      <div>
                        <div className="font-medium">{warehouse.name}</div>
                        <div className="text-muted-foreground text-sm capitalize">
                          {warehouse.storage_type} storage
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name={`initial_stocks.${index}.quantity`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Initial Stock</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0"
                                {...field}
                                onChange={(event) => field.onChange(event.target.value)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 md:hidden">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-xl"
                    onClick={() => {
                      form.reset(defaultValues);
                      onOpenChange(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-xl bg-foreground text-background hover:bg-foreground/90"
                  >
                    {isSubmitting ? "Saving Product..." : "Add Product"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="hidden border-t px-6 py-4 md:block">
              <div className="flex flex-col gap-3 md:flex-row md:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => {
                    form.reset(defaultValues);
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-xl bg-foreground text-background hover:bg-foreground/90"
                >
                  {isSubmitting ? "Saving Product..." : "Add Product"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
